

import React, { useState, useEffect } from 'react';
// Corrected import path for types.ts
import { Article, ArticleStatus, User } from '@/types'; // Corrected import path
import { Pencil, FileText, Calendar, Clock, AlertCircle } from 'lucide-react';

interface MyArticlesProps {
  currentUser: User;
  articles: Article[];
  onEdit: (article: Article) => void;
}

export const MyArticles: React.FC<MyArticlesProps> = ({ currentUser, articles, onEdit }) => {
  const myArticles = articles.filter(a => a.authorId === currentUser.id);

  const getStatusColor = (status: ArticleStatus) => {
    switch (status) {
      case ArticleStatus.APPROVED: return 'bg-green-100 text-green-800';
      case ArticleStatus.REJECTED: return 'bg-red-100 text-red-800';
      case ArticleStatus.PENDING: return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Meus Conhecimentos</h2>
          <p className="text-slate-500">Gerencie os artigos e procedimentos criados por você.</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {myArticles.length === 0 ? (
          <div className="p-12 text-center text-slate-400">
            <FileText className="w-16 h-16 mx-auto mb-4 opacity-20" />
            <p className="text-lg font-medium">Você ainda não publicou nenhum artigo.</p>
            <p className="text-sm mt-1">Clique em "Publicar" no menu lateral para começar.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4">Título do Artigo</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Última Atualização</th>
                  <th className="px-6 py-4 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {myArticles.map(article => (
                  <tr key={article.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium text-slate-800">{article.title}</div>
                      <div className="flex gap-2 mt-1">
                        {article.tags.map(tag => (
                          <span key={tag} className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded border border-slate-200">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(article.status)}`}>
                        {article.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-500">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(article.updatedAt).toLocaleDateString()}
                        <Clock className="w-3.5 h-3.5 ml-2" />
                        {new Date(article.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => onEdit(article)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors font-medium"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-yellow-800">
          <p className="font-medium mb-1">Importante:</p>
          <p>Ao editar um artigo que já estava "Aprovado", ele voltará automaticamente para o status <strong>"Pendente de Aprovação"</strong> para que as alterações sejam revisadas por um administrador.</p>
        </div>
      </div>
    </div>
  );
};